import { CloudUpload } from '@mui/icons-material';
import { Box, Button, Chip } from '@mui/material';
import classNames from 'classnames';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Spinner } from '../../../../components/loaders/Spinner';
import { notify } from '../../../../notifications';
import { InputProps } from '../../base';
import styles from './styles.module.css';

/** Maps Bootstrap-style size to MUI Button/Chip size. */
function toMuiSize(size?: string): 'small' | 'medium' | 'large' {
  if (size === 'sm') return 'small';
  if (size === 'lg') return 'large';
  return 'medium';
}

/** Maps Bootstrap-style size to MUI Chip size (Chip does not support large). */
function toChipSize(size?: string): 'small' | 'medium' {
  return size === 'sm' ? 'small' : 'medium';
}

/** Resolves the chip icon; Chip only accepts a ReactElement. */
function toChipIcon(loading: boolean | undefined, startIcon?: React.ReactNode): React.ReactElement {
  if (loading) {
    return <Spinner />;
  }

  if (React.isValidElement(startIcon)) {
    return startIcon;
  }

  return <CloudUpload />;
}

export type FileInputDisplayMode = 'button' | 'chip';

interface Props extends Omit<InputProps, 'onChange' | 'size'> {
  multiple?: boolean;
  onChange: (files: File[]) => void;
  loading?: boolean;
  size?: string;
  maxSizeMb?: number;
  helperText?: React.ReactNode;
  /** Icon shown on the button when not loading. Defaults to CloudUpload. */
  startIcon?: React.ReactNode;
  /** Visual trigger style. Defaults to `button`. */
  displayMode?: FileInputDisplayMode;
}

export type FileInputProps = Props;

export function FileInput(p: Props) {
  const { $t } = useIntl();
  const {
    name: passedName,
    onChange,
    accept,
    className,
    multiple = true,
    loading,
    size,
    maxSizeMb,
    helperText,
    startIcon,
    displayMode = 'button',
  } = p;

  const name = passedName || String(Date.now());
  const triggerIcon = loading ? <Spinner /> : (startIcon ?? <CloudUpload />);
  const chipIcon = toChipIcon(loading, startIcon);
  const triggerLabel = p.label || (
    <FormattedMessage id="common.fileInput.btnText" defaultMessage="Seleccionar archivos" />
  );

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files ? Array.from(event.target.files) : [];

    if (maxSizeMb && files.length && files.find((f) => f.size > maxSizeMb * 1024 * 1024)) {
      notify.error(
        $t(
          {
            id: 'common.fileInput.maxSizeExceeded',
            defaultMessage: 'Los archivos no deben superar {maxSizeMb} MB',
          },
          { maxSizeMb },
        ),
      );
      return;
    }

    onChange?.(files);
  }

  const fileInput = (
    <input
      type="file"
      multiple={multiple}
      id={name}
      name={name}
      onChange={handleFileChange}
      accept={accept}
      disabled={loading}
    />
  );

  return (
    <Box className={classNames(className, styles.wrapper)} display={'flex'} alignItems={'center'}>
      {displayMode === 'chip' ? (
        <Chip
          component="label"
          htmlFor={name}
          label={triggerLabel}
          icon={chipIcon}
          variant="outlined"
          size={toChipSize(size)}
          disabled={loading}
          clickable={!loading}
        />
      ) : (
        <Button
          component="label"
          htmlFor={name}
          variant="outlined"
          size={toMuiSize(size)}
          disabled={loading}
          startIcon={triggerIcon}
          sx={{ marginBottom: 0 }}
        >
          {triggerLabel}
        </Button>
      )}
      {fileInput}
      {maxSizeMb && (
        <Box ml={1}>
          {$t(
            {
              id: 'common.fileInput.maxSizeLabel',
              defaultMessage: 'Máx. {maxSizeMb} MB',
            },
            { maxSizeMb },
          )}
        </Box>
      )}
      {helperText && <Box ml={1}>{helperText}</Box>}
    </Box>
  );
}
